import EngageCampaignInterface from "@/interfaces/engageCampaign.interface";
import EngageContactInterface from "@/interfaces/engageContact.interface";
import { EngageLimitsInterface } from "@/interfaces/engageLimits.interface";
//@ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
//@ts-ignore
import i18n from "@/libs/i18n";
import { CampaignStats } from "@/types/campaign.types";
import { RequestQueryBuilder } from "@rewiko/crud-request";
//@ts-ignore
import { computed, readonly, Ref, ref, watch } from "@vue/composition-api";
import { AxiosResponse } from "axios";
//@ts-ignore
import { BTable } from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import { CampaignsInterface } from "./interfaces/campaigns";
import DeliveryStatsInterface from "@/interfaces/deliverytStats.interface";
import { DateTime } from "luxon";

const perPage = ref(10);
const total = ref(0);
const currentPage = ref(1);
const perPageOptions = [10, 25, 50, 100];
const searchQuery = ref("");
const sortBy = ref("createdAt");
const isSortDirDesc = ref(true);
const isLoading = ref(true);
const isSending = ref(false);
const campaigns: Ref<null | EngageCampaignInterface[]> = ref(null);
const refTable: Ref<BTable | null> = ref(null);
const isLoadingLimits = ref(true);
const campaignHasLoaded = ref(true);

const currentLimit = ref({
    contacts: {
        limit: 0,
        currentValue: 0,
        isLimitExceeded: false,
        isLimitReached: false,
    },
    messages: {
        limit: 0,
        currentValue: 0,
        isLimitExceeded: false,
        isLimitReached: false,
    },
    plannedContacts: {
        limit: 0,
        currentValue: 0,
        isLimitReached: false,
        isLimitExceeded: false,
    },
    plannedMessages: {
        limit: 0,
        currentValue: 0,
        isLimitReached: false,
        isLimitExceeded: false,
    },
});

const limitCheck = ref({
    contacts: {
        limit: 0,
        currentValue: 0,
        isLimitExceeded: false,
        isLimitReached: false,
    },
    messages: {
        limit: 0,
        currentValue: 0,
        isLimitExceeded: false,
        isLimitReached: false,
    },
    plannedContacts: {
        limit: 0,
        currentValue: 0,
        isReached: false,
        isExceeded: false,
    },
    plannedMessages: {
        limit: 0,
        currentValue: 0,
        isReached: false,
        isExceeded: false,
    },
});

const fetchCampaigns = (): Promise<EngageCampaignInterface[]> => {
    isLoading.value = true;
    const query = searchQuery.value;

    return axios
        .get("/campaigns", {
            params: RequestQueryBuilder.create({
                search: {
                    $or: [{ name: { $cont: query } }, { description: { $cont: query } }],
                },
                fields: ["id", "name", "description", "createdAt", "channelId", "sendAt"],
                limit: perPage.value,
                page: currentPage.value,
                sort: {
                    field: sortBy.value,
                    order: isSortDirDesc.value === true ? "DESC" : "ASC",
                },
            }).queryObject,
        })
        .then((response: any) => {
            const campaignsResponse = response.data?.data.map((response) => ({
                ...response,
                deliveryStats: { read: 0, delivered: 0, total: 0 },
            }));
            total.value = response.data.total;
            campaigns.value = !query && !campaignsResponse?.length ? undefined : campaignsResponse;
            fetchDeliveryStats().then((deliveryStats) => {
                campaigns.value = campaigns.value.map((campaign) =>
                    Object.assign({}, campaign, { deliveryStats: deliveryStats[campaign.id] })
                );
            });

            return response.data;
        })
        .finally(() => (isLoading.value = false));
};
const fetchDeliveryStats = (ids?: number[]): Promise<any> => {
    return axios
        .get("campaigns/delivery-stats", {
            params: { ids: ids ?? campaigns.value?.map((campaign) => campaign.id) },
        })
        .then((result) => result.data);
};

watch([currentPage, perPage], fetchCampaigns);

//@ts-ignore
const queryTimeout: Ref<NodeJS.Timeout | null> = ref(null);
watch(searchQuery, (query: string) => {
    if (query.length === 1) {
        return;
    }

    if (queryTimeout.value) {
        clearTimeout(queryTimeout.value);
    }

    queryTimeout.value = setTimeout(fetchCampaigns, 500);
});

export default function useCampaigns(bvModal = undefined) {
    const toast = useToast();
    const activeCampaign: Ref<null | EngageCampaignInterface> = ref(null);

    const dataMeta = computed(() => {
        const localItemsCount = refTable.value ? refTable.value.localItems.length : 0;

        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: total.value,
        };
    });

    const postCampaign = async (campaign: CampaignsInterface): Promise<CampaignsInterface | undefined> => {
        isSending.value = true;

        return await axios
            .post("/campaigns", campaign)
            .then((response: AxiosResponse) => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("engage.message-created"),
                        icon: "check",
                        variant: "success",
                    },
                });
                return response.data;
            })
            .catch(() => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("engage.new-engage-message"),
                        text: i18n.t("engage.overview.could-not-send"),
                        icon: "times",
                        variant: "danger",
                    },
                });
            })
            .finally(() => (isSending.value = false));
    };

    const putCampaign = async (campaign: CampaignsInterface): Promise<CampaignsInterface | undefined> => {
        isSending.value = true;
        return await axios
            .patch(`/campaigns/${campaign.id}`, campaign)
            .then((response: AxiosResponse) => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("engage.message-edited"),
                        icon: "times",
                        variant: "success",
                    },
                });
                return response.data;
            })
            .catch(() => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("engage-message-edited"),
                        text: i18n.t("engage.overview.could-not-send"),
                        icon: "times",
                        variant: "danger",
                    },
                });
            })
            .finally(() => (isSending.value = false));
    };

    const deleteCampaign = (campaignId: number) => {
        if (!bvModal) {
            throw new Error("campaigns not initialised correctly for deletion");
        }
        bvModal
            .msgBoxConfirm(i18n.t("confirm.Are you sure?"), {
                title: i18n.t("confirm.Are you sure?"),
                cancelTitle: i18n.t("confirm.Cancel"),
                cancelVariant: "light",
                okTitle: i18n.t("confirm.Yes, delete"),
                hideHeaderClose: false,
                headerCloseVariant: "light",
                centered: true,
                bodyClass: "mx-auto",
                headerClass: "text-center",
            })
            .then((result): unknown => {
                if (result) {
                    // we fetch the campaign because is a soft delete
                    getCampaignById(campaignId)
                        .then((res) => {
                            const campaignToDelete = { ...res, deletedAt: DateTime.now() };
                            axios
                                .delete(`/campaigns/${res.id}`)
                                .then((result) => {
                                    if (result) {
                                        toast.success(
                                            i18n.t("toast.delete.success", { entity: i18n.t("engage.label") })
                                        );
                                        fetchCampaigns().then();
                                        return result;
                                    }
                                })
                                .catch(() => {
                                    toast.error(i18n.t("toast.delete.error", { entity: i18n.t("engage.label") }));
                                });
                        })
                        .catch(() => {
                            toast.error(i18n.t("toast.delete.error", { entity: i18n.t("engage.label") }));
                        });
                }
                return false;
            });
    };

    const fetchActiveCampaign = (id: number): Promise<void> => {
        isLoading.value = true;

        return axios
            .get(`/campaigns/${id}`, {
                params: RequestQueryBuilder.create({
                    join: [
                        {
                            field: "contacts",
                        },
                        {
                            field: "contacts.message||status,statusReason",
                            select: ["message", "status", "statusReason"],
                        },
                        {
                            field: "attachments",
                        },
                    ],
                }).queryObject,
            })
            .then((response: AxiosResponse) => {
                activeCampaign.value = response.data;
            })
            .finally(() => (isLoading.value = false));
    };

    const getCampaignById = (id: number): Promise<CampaignsInterface> => {
        return axios
            .get(`/campaigns/${id}`, {
                params: RequestQueryBuilder.create({
                    join: [
                        {
                            field: "contacts",
                        },
                        {
                            field: "contacts.message||status,statusReason",
                            select: ["message", "status", "statusReason"],
                        },
                        {
                            field: "attachments",
                        },
                    ],
                }).queryObject,
            })
            .then(
                (
                    response: AxiosResponse<
                        Omit<CampaignsInterface, "sendAt" | "createdAt"> & { sendAt: string; createdAt: string }
                    >
                ) => {
                    return {
                        ...response.data,
                        createdAt: new Date(response.data.createdAt),
                        sendAt: DateTime.fromISO(response.data.sendAt),
                    } as CampaignsInterface;
                }
            );
    };

    const updateCampaignStats = (campaignId: number, deliveryStats: DeliveryStatsInterface) => {
        const targetCampaign = activeCampaign.value || campaigns.value?.find((campaign) => campaign.id === campaignId);

        if (targetCampaign) {
            targetCampaign.deliveryStats = deliveryStats;
        }
    };

    const updateDeliveryStats = ({
        campaignId,
        deliveryStats,
    }: {
        campaignId: number;
        deliveryStats: DeliveryStatsInterface;
    }) => {
        updateCampaignStats(campaignId, deliveryStats);
    };

    const fetchLimit = async (): Promise<void> => {
        isLoadingLimits.value = true;

        const result = axios
            .get("/campaigns/rate-limit")
            .then((response: AxiosResponse) => {
                currentLimit.value.contacts.limit = response.data.contacts.limit;
                currentLimit.value.contacts.currentValue = response.data.contacts.currentValue;
                currentLimit.value.contacts.isLimitExceeded = response.data.contacts.isExceeded;
                currentLimit.value.contacts.isLimitReached = response.data.contacts.isReached;
                currentLimit.value.messages.limit = response.data.messages.limit;
                currentLimit.value.messages.currentValue = response.data.messages.currentValue;
                currentLimit.value.messages.isLimitExceeded = response.data.messages.isExceeded;
                currentLimit.value.messages.isLimitReached = response.data.messages.isReached;
                currentLimit.value.plannedContacts.limit = response.data.plannedContacts.limit;
                currentLimit.value.plannedContacts.currentValue = response.data.plannedContacts.currentValue;
                currentLimit.value.plannedContacts.isLimitExceeded = response.data.plannedContacts.isExceeded;
                currentLimit.value.plannedContacts.isLimitReached = response.data.plannedContacts.isReached;
                currentLimit.value.plannedMessages.limit = response.data.plannedMessages.limit;
                currentLimit.value.plannedMessages.currentValue = response.data.plannedMessages.currentValue;
                currentLimit.value.plannedMessages.isLimitExceeded = response.data.plannedMessages.isExceeded;
                currentLimit.value.plannedMessages.isLimitReached = response.data.plannedMessages.isReached;
                return response.data;
            })
            .finally(() => (isLoadingLimits.value = false));

        return result;
    };

    const checkLimit = (
        contactsIds: number[]
    ): Promise<{ contacts: EngageLimitsInterface; messages: EngageLimitsInterface }> => {
        return axios.post("/campaigns/rate-limit", contactsIds).then((response: AxiosResponse) => {
            limitCheck.value.contacts.limit = response.data.contacts.limit;
            limitCheck.value.contacts.currentValue = response.data.contacts.currentValue;
            limitCheck.value.contacts.isLimitExceeded = response.data.contacts.isExceeded;
            limitCheck.value.contacts.isLimitReached = response.data.contacts.isReached;
            limitCheck.value.messages.limit = response.data.messages.limit;
            limitCheck.value.messages.currentValue = response.data.messages.currentValue;
            limitCheck.value.messages.isLimitExceeded = response.data.messages.isExceeded;
            limitCheck.value.messages.isLimitReached = response.data.messages.isReached;

            return response.data;
        });
    };

    const getDeliveredStats = (deliveryStats): CampaignStats => {
        // If no contacts were given just return 0 for everything
        if (!deliveryStats?.total) {
            return {
                target: 0,
                total: 0,
                ratio: 0,
            };
        }
        return {
            target: Number(deliveryStats.delivered) ?? 0,
            total: Number(deliveryStats.total),
            ratio: Math.round((Number(deliveryStats.delivered) / Number(deliveryStats.total)) * 1000) / 10 || 0,
        };
    };

    const getReadStats = (deliveryStats): CampaignStats => {
        // If no contacts were given just return 0 for everything
        if (!deliveryStats?.total) {
            return {
                target: 0,
                total: 0,
                ratio: 0,
            };
        }

        return {
            target: Number(deliveryStats.read) ?? 0,
            total: Number(deliveryStats.total),
            ratio: Math.round((Number(deliveryStats.read) / Number(deliveryStats.total)) * 1000) / 10 || 0,
        };
    };

    const updateDeliveredStatsFromEntry = ({ campaign }): void => {
        if (activeCampaign.value.id == campaign.campaignId) {
            updateCampaignStats(campaign.campaignId, campaign.deliveryStats);
        }
    };

    const ratesData = computed(() => {
        return {
            plannedContacts: currentLimit.value.plannedContacts.currentValue,
            PlannedConversations: currentLimit.value.plannedMessages.currentValue,
            TotalContacts: currentLimit.value.contacts.currentValue,
            TotalConversation: currentLimit.value.messages.currentValue,
        };
    });
    const campaignIsScheduled = computed(() => {
        return activeCampaign?.value?.status === "pending";
    });
    const deliveredStats = computed(() => getDeliveredStats(activeCampaign.value?.deliveryStats));
    const readStats = computed(() => getReadStats(activeCampaign.value?.deliveryStats));
    const sentCount = computed(() => {
        return (
            activeCampaign.value?.contacts.filter((contact: EngageContactInterface) => contact.status === "sent")
                .length || 0
        );
    });
    return {
        getDeliveredStats,
        getReadStats,
        postCampaign,
        putCampaign,
        fetchCampaigns,
        fetchActiveCampaign,
        updateDeliveryStats,
        updateDeliveredStatsFromEntry,
        fetchLimit,
        checkLimit,
        deleteCampaign,
        getCampaignById,
        perPage,
        total,
        currentPage,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        isLoading,
        campaignIsScheduled,
        isSending,
        campaigns: readonly(campaigns),
        refTable,
        dataMeta,
        activeCampaign,
        currentLimit,
        limitCheck,
        isLoadingLimits,
        ratesData,
        deliveredStats,
        readStats,
        sentCount,
    };
}
